import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { SheetHeader } from '../components/SheetHeader'
import { SheetMargin } from '../components/SheetMargin'
import { ENTITY, PLACE } from '../constants'
import { LEGAL_EFFECTIVE } from './meta'

type LegalLayoutProps = {
  title: string
  eyebrow: string
  summary: string
  documentTitle: string
  description: string
  children: ReactNode
}

export function LegalLayout({
  title,
  eyebrow,
  summary,
  documentTitle,
  description,
  children,
}: LegalLayoutProps) {
  useEffect(() => {
    document.title = documentTitle
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', description)
  }, [documentTitle, description])

  return (
    <>
      <SheetHeader />
      <main>
        <section className="bg-sheet">
          <div className="mx-auto max-w-[100rem] px-6 pt-6 pb-0 lg:px-8 lg:pt-8">
            <div className="cell bg-sheet">
              <div className="border-b border-rule px-5 py-5 sm:px-7 sm:py-7">
                <p className="cell-label mb-3 text-overprint">{eyebrow}</p>
                <h1 className="sheet-title text-ink">{title}</h1>
              </div>
              <div className="grid divide-y divide-rule lg:grid-cols-[1.4fr_1fr] lg:divide-x lg:divide-y-0">
                <p className="px-5 py-5 text-base leading-relaxed text-ink-muted sm:px-7 sm:py-6">
                  {summary}
                </p>
                <dl className="divide-y divide-rule-faint px-5 py-1 sm:px-7">
                  <div className="py-2.5">
                    <dt className="cell-label text-ink-muted">Controller</dt>
                    <dd className="mt-1 text-[0.9375rem] font-medium text-ink">
                      {ENTITY}
                    </dd>
                  </div>
                  <div className="py-2.5">
                    <dt className="cell-label text-ink-muted">Seat</dt>
                    <dd className="mt-1 text-[0.9375rem] font-medium text-ink">
                      {PLACE}
                    </dd>
                  </div>
                  <div className="py-2.5">
                    <dt className="cell-label text-ink-muted">Effective</dt>
                    <dd className="mt-1 text-[0.9375rem] font-medium text-ink">
                      {LEGAL_EFFECTIVE}
                    </dd>
                  </div>
                  <div className="py-2.5">
                    <dt className="cell-label text-ink-muted">Related</dt>
                    <dd className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[0.9375rem] font-medium text-ink">
                      <Link to="/terms" className="index-link">
                        Terms
                      </Link>
                      <Link to="/privacy" className="index-link">
                        Privacy
                      </Link>
                      <Link to="/cookies" className="index-link">
                        Cookies
                      </Link>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-sheet">
          <div className="mx-auto max-w-[100rem] px-6 py-10 lg:px-8 lg:py-14">
            <div className="cell divide-y divide-rule">{children}</div>
          </div>
        </section>
      </main>
      <SheetMargin variant="legal" />
    </>
  )
}

type LegalSectionProps = {
  code: string
  title: string
  children: ReactNode
}

export function LegalSection({ code, title, children }: LegalSectionProps) {
  return (
    <article className="grid gap-3 px-5 py-6 sm:px-7 lg:grid-cols-[5rem_14rem_1fr] lg:gap-8 lg:py-7">
      <span className="datum text-overprint">{code}</span>
      <h2 className="entry-title text-ink">{title}</h2>
      <div className="space-y-3 text-[0.9375rem] leading-relaxed text-ink-muted">
        {children}
      </div>
    </article>
  )
}
